import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiErrorDetail, scaricaFile } from "@/lib/api";
import { euro, dateIt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, Truck, HardHat, Search, FileDown, FileSpreadsheet, BedDouble, History, Fuel, Upload, Clock, Percent, AlertTriangle, Wrench, FileText, CopyCheck } from "lucide-react";
import { RiepilogoImportDialog, DuplicatiDialog, AnteprimaImportDialog, AvvisoSimili } from "@/components/DuplicatiAnagrafica";
import { MAGGIORAZIONI } from "@/lib/tipiOra";
import { StoricoAnagrafica } from "@/components/StoricoAnagrafica";
import { PanoramicaClientePreventivi } from "@/components/preventivi/PanoramicaClientePreventivi";

const FATT_FIELDS = [
  { k: "ragione_sociale", label: "Ragione Sociale", req: true, span: 2 },
  { k: "codice_fiscale", label: "Codice Fiscale" },
  { k: "piva", label: "Partita IVA" },
  { k: "indirizzo", label: "Via / Indirizzo", span: 2 },
  { k: "cap", label: "CAP" },
  { k: "citta", label: "Città" },
  { k: "provincia", label: "Provincia" },
  { k: "email", label: "Email" },
  { k: "pec", label: "PEC" },
  { k: "codice_sdi", label: "Codice SDI" },
  { k: "iban", label: "IBAN", span: 2 },
  { k: "telefono", label: "Telefono" },
  { k: "referente", label: "Referente" },
];

const CONFIG = {
  clienti: {
    label: "Clienti",
    singolare: "Cliente",
    icon: Building2,
    endpoint: "clienti",
    fields: [...FATT_FIELDS, { k: "settore", label: "Settore" }],
    columns: ["codice", "ragione_sociale", "piva", "citta", "referente", "telefono"],
    wide: true,
  },
  fornitori: {
    label: "Fornitori",
    singolare: "Fornitore",
    icon: Truck,
    endpoint: "fornitori",
    fields: [...FATT_FIELDS, { k: "categoria", label: "Categoria" }],
    columns: ["codice", "ragione_sociale", "piva", "citta", "referente", "telefono"],
    wide: true,
  },
  dipendenti: {
    label: "Dipendenti",
    singolare: "Dipendente",
    icon: HardHat,
    endpoint: "dipendenti",
    fields: [
      { k: "nome", label: "Nome", req: true },
      { k: "cognome", label: "Cognome", req: true },
      { k: "specializzazione", label: "Specializzazione" },
      { k: "tariffa_contratto_lordo", label: "Tariffa contratto lordo (€/ora, Ore Mensili)", type: "number" },
      { k: "costo_orario", label: "Costo orario commessa (lordo + tasse)", type: "calcolato", calcola: (f) => (Number(f.tariffa_contratto_lordo) || 0) * 2 },
      { k: "codice_fiscale", label: "Codice Fiscale" },
      { k: "telefono", label: "Numero di telefono" },
      { k: "email", label: "Email" },
      { k: "indirizzo", label: "Indirizzo completo", span: 2 },
      { k: "iban", label: "IBAN", span: 2 },
      { k: "data_assunzione", label: "Data di assunzione", type: "date" },
      { k: "data_scadenza_contratto", label: "Data scadenza contratto", type: "date" },
      { k: "tipo_contratto", label: "Tipologia di contratto", type: "select", options: [["indeterminato", "Tempo Indeterminato"], ["part_time", "Part-time"], ["determinato", "Tempo Determinato"], ["altro", "Altro"]], span: 2 },
      { k: "ore_giornaliere_contrattuali", label: "Ore giornaliere contrattuali (soglia avviso ore incomplete)", type: "number", step: "0.5", span: 2, visibile: (f) => f.tipo_contratto === "part_time" },
      { k: "tipo_accordo", label: "Tipo accordo (Ore Mensili)", type: "select", options: [["standard", "Standard"], ["netto_fisso", "Netto fisso"], ["stipendio_fisso", "Stipendio fisso"]], span: 2 },
      { k: "tetto_straordinari_mensili", label: "Tetto ore straordinarie mensili (solo Standard)", type: "number", step: "1" },
      { k: "tariffa_netta_accordata", label: "Tariffa netta accordata (€/ora, solo Netto fisso)", type: "number" },
      { k: "stipendio_fisso_mensile", label: "Stipendio fisso mensile (€, solo Stipendio fisso)", type: "number" },
    ],
    columns: ["codice", "nome", "cognome", "email", "specializzazione", "costo_orario", "costo_straordinario", "data_scadenza_contratto", "attivo"],
    wide: true,
  },
  veicoli: {
    label: "Veicoli",
    singolare: "Veicolo",
    icon: Truck,
    endpoint: "veicoli",
    fields: [
      { k: "targa", label: "Targa", req: true },
      { k: "modello", label: "Modello" },
      { k: "descrizione", label: "Descrizione" },
      { k: "costo_giornaliero", label: "Costo noleggio giornaliero (€)", type: "number" },
      { k: "consumo_l_100km", label: "Consumo medio (litri / 100 km)", type: "number" },
    ],
    columns: ["codice", "targa", "modello", "descrizione", "costo_giornaliero", "consumo_l_100km", "costo_litro", "attivo"],
  },
  servizi: {
    label: "Servizi",
    singolare: "Servizio",
    icon: Wrench,
    endpoint: "servizi",
    fields: [
      { k: "descrizione", label: "Descrizione", req: true, span: 2 },
      { k: "categoria", label: "Categoria (es. Noleggi, Subappalti, Manodopera specializzata)" },
      { k: "unita", label: "Unità di misura", type: "select", options: [["giorno", "giorno"], ["ora", "ora"], ["km", "km"], ["pz", "pz"], ["mese", "mese"], ["forfait", "forfait"], ["viaggio", "viaggio"], ["mq", "mq"], ["ml", "ml"], ["kg", "kg"]] },
      { k: "prezzo_riferimento", label: "Prezzo di riferimento (€)", type: "number", step: "0.01" },
    ],
    columns: ["codice", "descrizione", "categoria", "unita", "prezzo_riferimento", "attivo"],
    wide: true,
  },
};

const costoStraord = (r, magg) => (r.costo_orario || 0) * (1 + (magg ?? 25) / 100);

const HEADERS = {
  codice: "Codice",
  email: "Email",
  ragione_sociale: "Ragione Sociale",
  piva: "P. IVA",
  referente: "Referente",
  telefono: "Telefono",
  settore: "Settore",
  categoria: "Categoria",
  nome: "Nome",
  cognome: "Cognome",
  specializzazione: "Specializzazione",
  costo_orario: "€/h commessa",
  costo_straordinario: "€/h straord.",
  attivo: "Attivo",
  targa: "Targa",
  modello: "Modello",
  costo_giornaliero: "€/giorno",
  consumo_l_100km: "l/100 km",
  costo_litro: "€/litro",
  codice_articolo: "Cod. articolo",
  unita: "U.M.",
  prezzo_riferimento: "Prezzo rif.",
  fornitore_nome: "Fornitore abituale",
  citta: "Città",
  descrizione: "Descrizione",
  data_scadenza_contratto: "Scad. contratto",
};

function TariffaAziendale() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { data } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const [val, setVal] = useState(null);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put("/impostazioni", p)).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Tariffa oraria aziendale aggiornata: il costo delle ore è stato ricalcolato in tutto il gestionale"); setVal(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const current = val ?? data?.tariffa_oraria_aziendale ?? "";
  const attiva = Number(data?.tariffa_oraria_aziendale) > 0;
  const save = () => mut.mutate({ ...data, tariffa_oraria_aziendale: Number(current) || 0 });
  return (
    <div className="flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3" data-testid="tariffa-aziendale-card">
      <Clock className="h-5 w-5 text-primary" />
      <div className="text-sm text-slate-700 flex-1 min-w-[200px]">
        <span className="font-semibold">Tariffa oraria aziendale</span>
        <div className="text-xs text-slate-500">{attiva ? "Attiva: sostituisce il costo orario da contratto per tutte le ore lavorate (commesse, prestazioni, riepilogo mensile)." : "Non impostata (0): il costo delle ore viene calcolato dal contratto di ogni dipendente."}{!isAdmin && " Modificabile solo dagli Amministratori."}</div>
      </div>
      <Input type="number" min="0" step="0.5" value={current} onChange={(e) => setVal(e.target.value)} disabled={!isAdmin} className="w-24 bg-white" data-testid="tariffa-aziendale-input" />
      <span className="text-sm text-slate-500">€/h</span>
      {isAdmin && <Button size="sm" onClick={save} disabled={mut.isPending || val === null} data-testid="tariffa-aziendale-save-button">Salva</Button>}
    </div>
  );
}

const TURNI = [["primo", "Primo turno"], ["centrale", "Centrale"], ["secondo", "Secondo turno"], ["notturno", "Notturno"]];
function TurniPause() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { data } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const [bozza, setBozza] = useState(null);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put("/impostazioni", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["impostazioni"] }); toast.success("Turni e pause aggiornati"); setBozza(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const cur = bozza || { turni: data?.turni || {}, pause: data?.pause || {} };
  const setT = (k, campo, v) => setBozza({ ...cur, turni: { ...cur.turni, [k]: { ...(cur.turni[k] || {}), [campo]: v } } });
  const setP = (k, v) => setBozza({ ...cur, pause: { ...cur.pause, [k]: v } });
  if (!data) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 space-y-2" data-testid="turni-pause-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Clock className="h-4 w-4 text-primary" /> Turni e pause <span className="font-normal text-xs text-slate-500">— fasce orarie per il calcolo automatico del turno e pausa sottratta per luogo (Ore di Lavoro con ora inizio/fine).{!isAdmin && " Modificabili solo dagli Amministratori."}</span></div>
      <div className="flex flex-wrap gap-4 items-end">
        {TURNI.map(([k, l]) => (
          <div key={k} className="space-y-1" data-testid={`turno-${k}`}><Label className="text-xs text-slate-500">{l}</Label>
            <div className="flex items-center gap-1"><Input type="time" value={cur.turni[k]?.inizio || ""} onChange={(e) => setT(k, "inizio", e.target.value)} disabled={!isAdmin} className="w-28 bg-white" data-testid={`turno-${k}-inizio`} /><span className="text-slate-400">→</span><Input type="time" value={cur.turni[k]?.fine || ""} onChange={(e) => setT(k, "fine", e.target.value)} disabled={!isAdmin} className="w-28 bg-white" data-testid={`turno-${k}-fine`} /></div></div>
        ))}
        <div className="space-y-1"><Label className="text-xs text-slate-500">Pausa officina (min)</Label><Input type="number" min="0" step="5" value={cur.pause.officina ?? 30} onChange={(e) => setP("officina", e.target.value)} disabled={!isAdmin} className="w-24 bg-white" data-testid="pausa-officina" /></div>
        <div className="space-y-1"><Label className="text-xs text-slate-500">Pausa cantiere (min)</Label><Input type="number" min="0" step="5" value={cur.pause.cantiere ?? 60} onChange={(e) => setP("cantiere", e.target.value)} disabled={!isAdmin} className="w-24 bg-white" data-testid="pausa-cantiere" /></div>
        {isAdmin && <Button size="sm" disabled={!bozza || mut.isPending} onClick={() => mut.mutate({ ...data, turni: cur.turni, pause: { officina: Number(cur.pause.officina) || 0, cantiere: Number(cur.pause.cantiere) || 0 } })} data-testid="turni-pause-save">Salva</Button>}
      </div>
      <p className="text-xs text-slate-500">Festivo = domenica e festività nazionali (maggiorazione Domenica); Notturno usa la maggiorazione Notturno; sabato usa la maggiorazione Sabato.</p>
    </div>
  );
}


function MaggiorazioniOre() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { data } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const [bozza, setBozza] = useState(null);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put("/impostazioni", p)).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Maggiorazioni aggiornate: il costo delle ore già registrate è stato ricalcolato"); setBozza(null); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const magg = { ...(data?.maggiorazioni || {}), ...(bozza || {}) };
  const base = Number(data?.tariffa_oraria_aziendale) || 0;
  const save = () => mut.mutate({ ...data, maggiorazioni: Object.fromEntries(MAGGIORAZIONI.map((m) => [m.k, Number(magg[m.k]) || 0])) });
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="maggiorazioni-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Percent className="h-4 w-4 text-primary" /> Maggiorazioni ore <span className="font-normal text-xs text-slate-500">— valide per tutti i dipendenti, applicate alla tariffa base{base ? ` (${euro(base)}/h)` : " (costo orario da contratto)"}.{!isAdmin && " Modificabili solo dagli Amministratori."}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
        {MAGGIORAZIONI.map((m) => (
          <div key={m.k} className="space-y-1" data-testid={`magg-${m.k}`}>
            <Label className="text-xs">{m.label}</Label>
            <div className="flex items-center gap-1"><Input type="number" min="0" step="1" value={magg[m.k] ?? m.def} onChange={(e) => setBozza((b) => ({ ...(b || {}), [m.k]: e.target.value }))} disabled={!isAdmin} className="w-20 bg-white" data-testid={`magg-input-${m.k}`} /><span className="text-sm text-slate-500">%</span></div>
            {base > 0 && <div className="text-xs font-mono text-slate-500">= {euro(base * (1 + (Number(magg[m.k]) || 0) / 100))}/h</div>}
          </div>
        ))}
      </div>
      {isAdmin && <div className="mt-3 flex justify-end"><Button size="sm" onClick={save} disabled={mut.isPending || !bozza} data-testid="magg-save-button">Salva maggiorazioni</Button></div>}
    </div>
  );
}

function IndennitaNotte() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const [val, setVal] = useState(null);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put("/impostazioni", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["impostazioni"] }); toast.success("Indennità aggiornata"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const current = val ?? data?.indennita_notte ?? "";
  const [rimb, setRimb] = useState(null);
  const [cena, setCena] = useState(null);
  const currentRimb = rimb ?? data?.rimborso_pranzo_cantiere ?? "";
  const currentCena = cena ?? data?.rimborso_cena_trasferta ?? "";
  const save = () => mut.mutate({ ...data, indennita_notte: Number(current) || 0, costo_litro_default: data?.costo_litro_default || 0, rimborso_pranzo_cantiere: Number(currentRimb) || 0, rimborso_cena_trasferta: Number(currentCena) || 0 });
  return (
    <div className="flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3" data-testid="indennita-card">
      <BedDouble className="h-5 w-5 text-primary" />
      <div className="text-sm text-slate-700 flex-1 min-w-[200px]">
        <span className="font-semibold">Indennità e rimborsi</span>
        <div className="text-xs text-slate-500">Indennità per notte fuori sede, Pasto cantiere e Cena trasferta (costi della commessa).</div>
      </div>
      <Label className="text-xs text-slate-500">Notte</Label>
      <Input type="number" min="0" step="0.5" value={current} onChange={(e) => setVal(e.target.value)} className="w-24 bg-white" data-testid="indennita-input" />
      <span className="text-sm text-slate-500">€/notte</span>
      <Label className="text-xs text-slate-500 ml-2">Pasto cantiere</Label>
      <Input type="number" min="0" step="0.5" value={currentRimb} onChange={(e) => setRimb(e.target.value)} className="w-24 bg-white" data-testid="rimborso-pranzo-input" />
      <span className="text-sm text-slate-500">€/giorno</span>
      <Label className="text-xs text-slate-500 ml-2">Cena trasferta</Label>
      <Input type="number" min="0" step="0.5" value={currentCena} onChange={(e) => setCena(e.target.value)} className="w-24 bg-white" data-testid="rimborso-cena-input" />
      <span className="text-sm text-slate-500">€/notte</span>
      <Button size="sm" onClick={save} disabled={mut.isPending} data-testid="indennita-save-button">Salva</Button>
    </div>
  );
}

function CarburanteGlobale() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const [litro, setLitro] = useState(null);
  const mut = useMutation({
    mutationFn: async (p) => (await api.put("/impostazioni", p)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["impostazioni"] });
      qc.invalidateQueries({ queryKey: ["veicoli"] });
      toast.success("Prezzo carburante aggiornato su tutti i veicoli");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const currentLitro = litro ?? data?.costo_litro_default ?? "";
  const save = () => mut.mutate({ ...data, indennita_notte: data?.indennita_notte ?? 20, costo_litro_default: Number(currentLitro) || 0, rimborso_pranzo_cantiere: data?.rimborso_pranzo_cantiere ?? 20, rimborso_cena_trasferta: data?.rimborso_cena_trasferta ?? 20 });
  return (
    <div className="flex flex-wrap items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3" data-testid="carburante-card">
      <Fuel className="h-5 w-5 text-primary" />
      <div className="text-sm text-slate-700 flex-1 min-w-[200px]">
        <span className="font-semibold">Carburante €/litro</span>
        <div className="text-xs text-slate-500">Prezzo applicato a tutti i veicoli per il calcolo del carburante.</div>
      </div>
      <Input type="number" min="0" step="0.01" value={currentLitro} onChange={(e) => setLitro(e.target.value)} className="w-24 bg-white" data-testid="costo-litro-input" />
      <span className="text-sm text-slate-500">€/litro</span>
      <Button size="sm" onClick={save} disabled={mut.isPending} data-testid="costo-litro-save-button">Salva</Button>
    </div>
  );
}

function Section({ kind }) {
  const cfg = CONFIG[kind];
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");
  const [soloAttivi, setSoloAttivi] = useState(false);
  const [storico, setStorico] = useState(null);
  const [panoramica, setPanoramica] = useState(null);
  const [riepilogoImport, setRiepilogoImport] = useState(null);
  const [anteprima, setAnteprima] = useState(null);
  const [fileImport, setFileImport] = useState(null);
  const [duplicati, setDuplicati] = useState(false);
  const { data: impostazioniGlobali } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data, enabled: kind === "dipendenti" });
  const maggStr = impostazioniGlobali?.maggiorazioni?.straordinario ?? 25;

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [cfg.endpoint],
    queryFn: async () => (await api.get(`/${cfg.endpoint}`)).data,
  });

  const isSoggetto = kind === "clienti" || kind === "fornitori";
  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (isSoggetto) return (await api.post("/soggetti", payload)).data;
      if (editing) return (await api.put(`/${cfg.endpoint}/${editing.id}`, payload)).data;
      return (await api.post(`/${cfg.endpoint}`, payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [cfg.endpoint] });
      if (isSoggetto) {
        qc.invalidateQueries({ queryKey: ["clienti"] });
        qc.invalidateQueries({ queryKey: ["fornitori"] });
      }
      toast.success(editing ? "Modifiche salvate" : `${cfg.singolare} aggiunto`);
      setOpen(false);
      setEditing(null);
      setForm({});
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const importMut = useMutation({
    mutationFn: async ({ file, anteprima }) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(kind === "servizi" ? "/servizi/import" : `/soggetti/import?ruolo_default=${kind === "fornitori" ? "fornitore" : "cliente"}&anteprima=${anteprima ? "true" : "false"}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (r) => {
      if (r.anteprima) { setAnteprima(r); return; }
      setAnteprima(null); setFileImport(null);
      qc.invalidateQueries({ queryKey: ["clienti"] });
      qc.invalidateQueries({ queryKey: ["fornitori"] });
      qc.invalidateQueries({ queryKey: ["servizi"] });
      if (isSoggetto) { setRiepilogoImport(r); qc.invalidateQueries({ queryKey: ["soggetti-duplicati"] }); return; }
      toast.success(`Importazione completata: ${r.creati ?? r.importati} creati, ${r.aggiornati} aggiornati${r.saltati ? `, ${r.saltati} righe vuote saltate` : ""}`);
      if (r.mappatura) toast.info(`Colonne riconosciute: ${Object.entries(r.mappatura).map(([campo, col]) => `${col} → ${campo}`).join(", ")}`, { duration: 12000 });
      if (r.errori?.length) toast.error(`${r.errori.length} righe non importate: ${r.errori.slice(0, 3).join("; ")}${r.errori.length > 3 ? "…" : ""}`, { duration: 10000 });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/${cfg.endpoint}/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [cfg.endpoint] });
      toast.success("Eliminato");
    },
  });

  const openNew = () => {
    setEditing(null);
    setForm(kind === "dipendenti" || kind === "veicoli" || kind === "servizi" ? { attivo: true, ...(kind === "servizi" ? { unita: "giorno" } : {}) } : isSoggetto ? { is_cliente: kind === "clienti", is_fornitore: kind === "fornitori" } : {});
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r });
    setOpen(true);
  };

  const submit = () => {
    const missing = cfg.fields.filter((f) => f.req && !form[f.k]);
    if (missing.length) {
      toast.error(`Compila: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    const payload = {};
    cfg.fields.forEach((f) => {
      if (f.type === "calcolato") return;
      payload[f.k] = f.type === "number" ? (f.step === "1" ? Math.round(Number(form[f.k]) || 0) : Number(form[f.k]) || 0) : form[f.k] || "";
    });
    if (kind === "dipendenti" || kind === "veicoli" || kind === "servizi") payload.attivo = form.attivo !== false;
    if (isSoggetto) {
      if (!form.is_cliente && !form.is_fornitore) return toast.error("Seleziona almeno una opzione tra 'È cliente' e 'È fornitore'");
      payload.is_cliente = !!form.is_cliente;
      payload.is_fornitore = !!form.is_fornitore;
      payload.cliente_id = editing ? (kind === "clienti" ? editing.id : editing.cliente_id || null) : null;
      payload.fornitore_id = editing ? (kind === "fornitori" ? editing.id : editing.fornitore_id || null) : null;
    }
    saveMut.mutate(payload);
  };

  const renderCell = (r, col) => {
    if (col === "email") return r.email ? <span className="text-xs">{r.email}</span> : <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title="Senza email il PDF Ore Mensili non può essere inviato" data-testid={`email-mancante-${r.codice}`}><AlertTriangle className="h-3 w-3" /> Email mancante</span>;
    if (col === "prezzo_riferimento") return r[col] ? euro(r[col]) : <span className="text-slate-300">—</span>;
    if (col === "fornitore_nome") return r[col] || <span className="text-slate-300">—</span>;
    if (col === "costo_orario" || col === "costo_giornaliero" || col === "costo_litro") return euro(r[col]);
    if (col === "costo_straordinario") return <span title={`Maggiorazione globale +${maggStr}%`}>{euro(costoStraord(r, maggStr))} <span className="text-xs text-slate-400">(+{maggStr}%)</span></span>;
    if (col === "data_scadenza_contratto") {
      if (!r[col]) return <span className="text-slate-300">—</span>;
      const giorni = Math.ceil((new Date(r[col]) - new Date()) / 86400000);
      return <span className={giorni <= 7 ? "text-red-600 font-semibold" : ""}>{dateIt(r[col])}</span>;
    }
    if (col === "consumo_l_100km") return `${r[col] ?? 0} l`;
    if (col === "attivo")
      return r[col] ? (
        <span className="text-emerald-600 text-xs font-medium">Attivo</span>
      ) : (
        <span className="text-slate-400 text-xs">Non attivo</span>
      );
    if (col === "codice") return <span className="font-mono text-xs text-slate-500">{r[col]}</span>;
    if (col === "ragione_sociale")
      return (
        <span className="inline-flex items-center gap-2">
          {r[col] || "—"}
          {kind === "clienti" && r.is_fornitore && <span className="text-[11px] font-medium text-primary bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5" data-testid={`badge-anche-fornitore-${r.codice}`}>anche fornitore</span>}
          {kind === "fornitori" && r.is_cliente && <span className="text-[11px] font-medium text-primary bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5" data-testid={`badge-anche-cliente-${r.codice}`}>anche cliente</span>}
        </span>
      );
    return r[col] || "—";
  };

  const filtered = rows.filter((r) => {
    if (kind === "dipendenti" && soloAttivi && r.attivo === false) return false;
    if (kind === "veicoli" && soloAttivi && r.attivo === false) return false;
    if (!search) return true;
    const hay = Object.values(r).join(" ").toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-4">
      {kind === "dipendenti" && <><TariffaAziendale /><MaggiorazioniOre /><TurniPause /><IndennitaNotte /></>}
      {kind === "dipendenti" && rows.some((r) => r.attivo !== false && !r.email) && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm" data-testid="dipendenti-email-mancanti-avviso">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div><b>{rows.filter((r) => r.attivo !== false && !r.email).length} dipendenti attivi senza email</b>: {rows.filter((r) => r.attivo !== false && !r.email).map((r) => `${r.nome} ${r.cognome}`).join(", ")}. Senza email non riceveranno il PDF Ore Mensili: apri la scheda e inserisci l'indirizzo.</div>
        </div>
      )}
      {kind === "veicoli" && <CarburanteGlobale />}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={`Cerca in ${cfg.label.toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white"
            data-testid={`search-${kind}`}
          />
        </div>
        {(kind === "dipendenti" || kind === "veicoli") && (
          <button
            onClick={() => setSoloAttivi((v) => !v)}
            data-testid="filter-attivi"
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              soloAttivi
                ? "bg-primary text-white"
                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            Solo attivi
          </button>
        )}
        {kind === "dipendenti" && (
          <Button variant="outline" onClick={() => scaricaFile(`${API}/dipendenti/export/excel`, (m) => toast.error(m))} data-testid="dipendenti-excel-button">
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
          </Button>
        )}
        {(isSoggetto || kind === "servizi") && (
          <>
            <input
              type="file"
              accept=".xlsx"
              className="hidden"
              id={`import-${kind}`}
              data-testid={`import-excel-input-${kind}`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { setFileImport(f); importMut.mutate({ file: f, anteprima: isSoggetto }); }
                e.target.value = "";
              }}
            />
            <Button variant="outline" onClick={() => scaricaFile(kind === "servizi" ? `${API}/servizi/import/modello` : `${API}/soggetti/import/modello`, (m) => toast.error(m))} data-testid={`scarica-modello-button-${kind}`}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Scarica modello
            </Button>
            <Button variant="outline" disabled={importMut.isPending} onClick={() => document.getElementById(`import-${kind}`).click()} data-testid={`import-excel-button-${kind}`}>
              <Upload className="h-4 w-4 mr-1.5" /> {importMut.isPending ? "Importazione…" : "Importa da Excel"}
            </Button>
            {isSoggetto && <Button variant="outline" onClick={() => setDuplicati(true)} data-testid={`controlla-duplicati-button-${kind}`}><CopyCheck className="h-4 w-4 mr-1.5" /> Controlla duplicati</Button>}
          </>
        )}
        <Button onClick={openNew} data-testid={`add-${kind}-button`}>
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        {isLoading ? (
          <div className="py-12 text-center text-slate-400">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-400">Nessun record.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                {cfg.columns.map((col) => (
                  <th key={col} className="px-4 py-3">
                    {HEADERS[col]}
                  </th>
                ))}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`${kind}-row-${r.codice}`}>
                  {cfg.columns.map((col) => (
                    <td key={col} className="px-4 py-3 text-slate-700">
                      {renderCell(r, col)}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {kind === "clienti" && (
                      <button onClick={() => setPanoramica(r)} className="text-slate-300 hover:text-primary mr-3" title="Preventivi del cliente" data-testid={`preventivi-cliente-${r.codice}`}>
                        <FileText className="h-4 w-4" />
                      </button>
                    )}
                    {(kind === "clienti" || kind === "fornitori") && (
                      <button
                        onClick={() => setStorico(r)}
                        className="text-slate-300 hover:text-primary mr-3"
                        title="Scheda e storico"
                        data-testid={`storico-${kind}-${r.codice}`}
                      >
                        <History className="h-4 w-4" />
                      </button>
                    )}
                    {kind === "clienti" && (
                      <>
                        <button
                          onClick={() => scaricaFile(`${API}/report/clienti/pdf?cliente_id=${r.id}`, (m) => toast.error(m))}
                          className="text-slate-300 hover:text-primary mr-3"
                          title="Report PDF cliente"
                          data-testid={`report-pdf-cliente-${r.codice}`}
                        >
                          <FileDown className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => scaricaFile(`${API}/report/clienti/excel?cliente_id=${r.id}`, (m) => toast.error(m))}
                          className="text-slate-300 hover:text-primary mr-3"
                          title="Report Excel cliente"
                          data-testid={`report-excel-cliente-${r.codice}`}
                        >
                          <FileSpreadsheet className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => openEdit(r)}
                      className="text-slate-300 hover:text-primary mr-3"
                      data-testid={`edit-${kind}-${r.codice}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    {(isAdmin || kind !== "dipendenti") && (
                    <button
                      onClick={() => delMut.mutate(r.id)}
                      className="text-slate-300 hover:text-red-600"
                      data-testid={`delete-${kind}-${r.codice}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${cfg.wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Modifica" : "Nuovo"} {cfg.singolare.toLowerCase()}
            </DialogTitle>
          </DialogHeader>
          <div className={cfg.wide ? "grid sm:grid-cols-2 gap-3" : "space-y-3"}>
            {isSoggetto && (
              <div className="sm:col-span-2 flex flex-wrap gap-6 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2.5" data-testid="soggetto-tipo-checkbox">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                  <Checkbox checked={!!form.is_cliente} onCheckedChange={(v) => setForm((s) => ({ ...s, is_cliente: !!v }))} data-testid="is-cliente-checkbox" />
                  È cliente
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                  <Checkbox checked={!!form.is_fornitore} onCheckedChange={(v) => setForm((s) => ({ ...s, is_fornitore: !!v }))} data-testid="is-fornitore-checkbox" />
                  È fornitore
                </label>
              </div>
            )}
            {cfg.fields.filter((f) => !f.visibile || f.visibile(form)).map((f) => (
              <div key={f.k} className={`space-y-1.5 ${f.span === 2 ? "sm:col-span-2" : ""}`}>
                <Label>
                  {f.label} {f.req && "*"}
                </Label>
                {f.type === "calcolato" ? (
                  <Input readOnly value={euro(f.calcola(form))} className="bg-slate-50 text-slate-700 font-mono" title="Calcolato automaticamente: tariffa contratto lordo × 2" data-testid={`${kind}-field-${f.k}`} />
                ) : f.type === "select" ? (
                  <Select value={form[f.k] || ""} onValueChange={(v) => setForm((s) => ({ ...s, [f.k]: v, ...(f.k === "tipo_contratto" && v === "part_time" && (!s.ore_giornaliere_contrattuali || Number(s.ore_giornaliere_contrattuali) >= 8) ? { ore_giornaliere_contrattuali: 4 } : {}) }))}>
                    <SelectTrigger data-testid={`${kind}-field-${f.k}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                <Input
                  type={f.type || "text"}
                  step={f.step}
                  value={form[f.k] ?? ""}
                  onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                  data-testid={`${kind}-field-${f.k}`}
                />
                )}
                {isSoggetto && f.k === "ragione_sociale" && <AvvisoSimili nome={form.ragione_sociale} piva={form.piva} escludi={[editing?.id, editing?.cliente_id, editing?.fornitore_id].filter(Boolean).join(",")} />}
              </div>
            ))}
            {kind === "dipendenti" && (
              <div className="text-sm text-slate-600 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2" data-testid="dipendente-costo-straordinario">
                Costo orario straordinario calcolato (maggiorazione globale +{maggStr}%): <b className="font-mono text-primary">{euro(costoStraord({ costo_orario: (Number(form.tariffa_contratto_lordo) || 0) * 2 }, maggStr))}</b>
              </div>
            )}
            {(kind === "dipendenti" || kind === "veicoli") && (
              <div className="flex items-center justify-between pt-1">
                <Label>{kind === "veicoli" ? "Veicolo attivo" : kind === "servizi" ? "Servizio attivo (selezionabile nelle fatture)" : "Dipendente attivo"}</Label>
                <Switch
                  checked={form.attivo !== false}
                  onCheckedChange={(v) => setForm((s) => ({ ...s, attivo: v }))}
                  data-testid="dipendente-attivo-switch"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              disabled={saveMut.isPending}
             
              data-testid={`${kind}-save-button`}
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {storico && (
        <StoricoAnagrafica kind={kind} record={storico} open={!!storico} onOpenChange={(v) => !v && setStorico(null)} />
      )}
      {panoramica && <PanoramicaClientePreventivi cliente={panoramica} open onOpenChange={(v) => !v && setPanoramica(null)} />}
      {riepilogoImport && <RiepilogoImportDialog r={riepilogoImport} onClose={() => setRiepilogoImport(null)} />}
      {anteprima && <AnteprimaImportDialog r={anteprima} loading={importMut.isPending} onClose={() => { setAnteprima(null); setFileImport(null); }} onConferma={() => importMut.mutate({ file: fileImport, anteprima: false })} />}
      {duplicati && <DuplicatiDialog kind={kind} isAdmin={isAdmin} onClose={() => setDuplicati(false)} onEdit={(id) => { const r = rows.find((x) => x.id === id); if (r) { setDuplicati(false); openEdit(r); } }} />}
    </div>
  );
}

export default function Anagrafica() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Anagrafica
        </h1>
        <p className="text-slate-500 mt-1">Clienti, fornitori, dipendenti e veicoli dell'azienda.</p>
      </div>
      <Tabs defaultValue="clienti">
        <TabsList className="bg-white border border-slate-200">
          {Object.entries(CONFIG).map(([k, cfg]) => (
            <TabsTrigger key={k} value={k} data-testid={`anagrafica-tab-${k}`}>
              <cfg.icon className="h-4 w-4 mr-1.5" />
              {cfg.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(CONFIG).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <Section kind={k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
